import {
  AnimatedSprite,
  Application,
  Assets,
  Container,
  Rectangle,
  Texture,
  Cache,
} from "pixi.js";
import { Position } from "types/battle-engine/battle";
import { VFX_SPRITE_FRAME_COUNT_DATA } from "@/data/vfx-sprite-frame-count-data";
import { VFX_SPRITE_FRAME_ANCHOR_DATA } from "@/data/vfx-sprite-frame-anchor-data";
import { ClientPokemon } from "./client-pokemon";

export interface VFXConfig {
  app: Application;
  spriteUrl: string;
  spriteFrameCount: number;
  spriteScale: number;
  spriteScaleX: number;
  spriteScaleY: number;
  spriteAlpha: number;
  position: Position;
  parent?: Container;
  loops: number;
  speed: number;
  ping_pong: boolean;
  anchorTarget?: ClientPokemon | null;
  y_offset?: number;
  falling?: boolean;
  duration_ticks?: number;
}

const DEFAULT_FPS = 24;

/**
 * tVFX class handles visual effects instantiation
 * - Plays animation at target position
 * - Auto-destroys after animation completes
 * - Supports animated sprite strips (plays once)
 */
export class VFX {
  private animatedSprite: AnimatedSprite | null = null;
  private container: Container;
  private parent: Container;
  private isDestroyed: boolean = false;
  private spriteUrl: string;
  private spriteFrameCount: number;
  private spriteScale: number;
  private spriteScaleX: number;
  private spriteScaleY: number;
  private spriteAlpha: number;
  private loops: number;
  private speed: number;
  private ping_pong: boolean;
  private anchorTarget?: ClientPokemon | null;
  private y_offset?: number;
  private falling?: boolean;
  private duration_ticks?: number;

  constructor(config: VFXConfig) {
    this.parent = config.parent ?? config.app.stage;
    this.container = new Container();
    this.container.x = config.position.x;
    this.container.y = config.position.y;
    this.container.zIndex = 9998; // Render on top but below projectiles
    this.spriteUrl = config.spriteUrl;
    this.spriteFrameCount = config.spriteFrameCount;
    this.spriteScale = config.spriteScale ?? 1;
    this.spriteScaleX = config.spriteScaleX ?? 1;
    this.spriteScaleY = config.spriteScaleY ?? 1;
    this.spriteAlpha = config.spriteAlpha ?? 1;
    this.loops = config.loops ?? 1;
    this.speed = config.speed ?? 1;
    this.ping_pong = config.ping_pong ?? false;
    this.anchorTarget = config.anchorTarget ?? null;
    this.y_offset = config.y_offset ?? 0;
    this.falling = config.falling ?? false;
    this.duration_ticks = config.duration_ticks ?? 0;
  }

  public async play() {
    this.parent.addChild(this.container);
    this.animatedSprite!.loop = this.loops > 1;
    this.animatedSprite!.animationSpeed = this.speed * 0.8;
    this.container.pivot.set(0.5, 0.5);

    if (this.speed < 0) {
      this.animatedSprite!.gotoAndPlay(this.animatedSprite!.totalFrames - 1);
    } else {
      this.animatedSprite!.play();
    }

    // If anchored to target, update position to target position on each tick
    if (this.anchorTarget) {
      const startYOffset = this.y_offset ?? 0;
      const totalTicks = Math.max(1, this.duration_ticks ?? 0); // avoid div by zero
      let tick = 0;

      this.animatedSprite!.onRender = () => {
        if (!this.anchorTarget || this.anchorTarget.IsDestroyed) return;

        if (this.falling && this.duration_ticks && startYOffset !== 0) {
          const progress = Math.min(tick / totalTicks, 1); // 0..1
          const currentYOffset = startYOffset * (1 - progress); // move toward 0
          // this.animatedSprite!.alpha = Math.pow(progress, 2); // fade out
          // apply position using currentYOffset (do NOT overwrite this.y_offset)
          this.container.x = this.anchorTarget.Position.x;
          this.container.y = this.anchorTarget.Position.y + currentYOffset;
          this.container.zIndex = this.anchorTarget.Position.y;

          if (progress >= 1) {
            this.destroy();
            return;
          }
        } else {
          // anchored but not falling: keep constant offset
          this.container.x = this.anchorTarget.Position.x;
          this.container.y =
            this.anchorTarget.Position.y + (this.y_offset ?? 0);
          this.container.zIndex = this.anchorTarget.Position.y;
        }

        tick++;
      };
    }

    let loopCount = 1;
    this.animatedSprite!.onLoop = () => {
      if (this.ping_pong && loopCount > 1) {
        this.animatedSprite!.animationSpeed =
          -this.animatedSprite!.animationSpeed;
      }

      if (loopCount >= this.loops) {
        this.destroy();
      }

      loopCount++;
    };
  }

  /**
   * Load impact sprite and set up animation
   * Loads optional JSON metadata for animation
   */
  public async load() {
    try {
      // Resolve the URL properly for Vite
      const texture = Cache.has(this.spriteUrl)
        ? Cache.get(this.spriteUrl)
        : await Assets.load<Texture>(this.spriteUrl);

      const frames = this.createFrames(texture, this.spriteUrl);

      const anchor = VFX_SPRITE_FRAME_ANCHOR_DATA[this.spriteUrl] ?? {
        x: 0.5,
        y: 0.5,
      };

      this.animatedSprite = new AnimatedSprite(frames);
      this.animatedSprite.anchor.set(anchor.x, anchor.y);
      this.animatedSprite.scale.set(
        this.spriteScale * 1.2 * this.spriteScaleX,
        this.spriteScale * 1.2 * this.spriteScaleY,
      );
      this.animatedSprite.alpha = this.spriteAlpha;
      this.animatedSprite.loop = false; // Impacts play once
      this.animatedSprite.animationSpeed =
        (this.spriteFrameCount || DEFAULT_FPS) / 60;

      this.animatedSprite.onComplete = () => {
        this.destroy();
      };

      this.container.addChild(this.animatedSprite);
    } catch (error) {
      console.error("Failed to load VFX sprite:", this.spriteUrl, error);
      this.destroy();
    }
  }

  /**
   * Create animation frames from sprite strip
   * If frameCount not specified, treats as single frame
   */
  private createFrames(texture: Texture, spriteUrl: string): Texture[] {
    const frameCount = VFX_SPRITE_FRAME_COUNT_DATA[spriteUrl];

    if (!frameCount)
      throw new Error(`Frame count not found for sprite: ${spriteUrl}`);

    const frames: Texture[] = [];
    const frameWidth = texture.width / frameCount;
    const frameHeight = texture.height;

    for (let i = 0; i < frameCount; i++) {
      if (Cache.has(spriteUrl + "__frame-" + i)) {
        const cachedTexture = Cache.get(spriteUrl + "__frame-" + i);
        frames.push(cachedTexture);
        continue;
      }

      const frame = new Texture({
        label: spriteUrl + "__frame-" + i,
        source: texture.source,
        frame: new Rectangle(i * frameWidth, 0, frameWidth, frameHeight),
      });
      frames.push(frame);
      Cache.set(spriteUrl + "__frame-" + i, frame);
    }

    return frames;
  }

  /**
   * Clean up resources and remove from stage
   */
  public destroy() {
    if (this.isDestroyed) return;
    this.isDestroyed = true;

    this.container.destroy({ children: true });
  }
}
